import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useCompany } from '@/contexts/CompanyContext'
import { useAuth } from '@/hooks/useAuth'
import { ChevronDown, Building2, Check } from 'lucide-react'

interface UserCompany {
  company_id: string
  role: string
  is_active: boolean
  company: {
    id: string
    name: string
    slug: string
  }
}

export const CompanySwitcher: React.FC = () => {
  const { currentCompany } = useCompany()
  const { user, isSuperAdmin } = useAuth()
  const [isOpen, setIsOpen] = useState(false)

  // Fetch all companies the user belongs to (or ALL companies if super admin)
  const { data: userCompanies = [] } = useQuery({
    queryKey: ['user-companies', user?.id, isSuperAdmin],
    queryFn: async () => {
      if (!user?.id) return []

      // Super admin can see ALL companies in the database
      if (isSuperAdmin) {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name, slug')
          .order('name', { ascending: true })

        if (error) throw error

        // Map to UserCompany format with super_admin role
        return (data || []).map(company => ({
          company_id: company.id,
          role: 'super_admin',
          is_active: true,
          company: company
        })) as UserCompany[]
      }

      // Regular users only see companies they're members of
      const { data, error } = await supabase
        .from('user_companies')
        .select('company_id, role, is_active, company:companies(id, name, slug)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('joined_at', { ascending: false })

      if (error) throw error
      // Normalize company field - Supabase returns it as an array for foreign keys
      return (data || []).map(item => ({
        ...item,
        company: Array.isArray(item.company) ? item.company[0] : item.company
      })) as UserCompany[]
    },
    enabled: !!user?.id
  })

  const handleSwitch = async (companySlug: string) => {
    setIsOpen(false)

    // Get the current subdomain
    const hostname = window.location.hostname
    const parts = hostname.split('.')

    // Determine if we're on localhost or a real domain
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'

    if (isLocalhost) {
      // On localhost, use query parameter for company switching
      const url = new URL(window.location.href)
      url.searchParams.set('company', companySlug)
      window.location.href = url.toString()
    } else {
      // On production, redirect to the company's subdomain
      const baseDomain = parts.length > 2 ? parts.slice(-2).join('.') : hostname
      window.location.href = `https://${companySlug}.${baseDomain}`
    }
  }

  // Super admin always sees the switcher (to switch between all companies)
  // Regular users only see it if they have multiple companies
  if (!isSuperAdmin && userCompanies.length <= 1) return null

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
      >
        <Building2 className="h-4 w-4 text-muted" />
        <span className="font-medium text-strong max-w-[150px] truncate">
          {currentCompany?.name || 'Select Company'}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-[var(--color-surface)] border border-subtle rounded-lg shadow-floating z-50 py-2">
            <div className="px-3 py-2 border-b border-subtle">
              <p className="text-xs font-semibold text-muted uppercase">
                {isSuperAdmin ? 'All Companies (Super Admin)' : 'Your Companies'}
              </p>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {userCompanies.map(({ company, role }) => {
                const isCurrentCompany = currentCompany?.id === company.id

                return (
                  <button
                    key={company.id}
                    onClick={() => !isCurrentCompany && handleSwitch(company.slug)}
                    disabled={isCurrentCompany}
                    className={`w-full text-left px-3 py-2.5 hover:bg-[var(--color-surface-hover)] transition-colors flex items-center justify-between gap-3 ${
                      isCurrentCompany ? 'bg-[var(--color-primary-soft)] cursor-default' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-strong truncate">
                        {company.name}
                      </div>
                      <div className="text-xs text-muted capitalize">
                        {role}
                      </div>
                    </div>

                    {isCurrentCompany && (
                      <Check className="h-4 w-4 text-[var(--color-primary)] flex-shrink-0" />
                    )}
                  </button>
                )
              })}
            </div>

            {userCompanies.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted">
                No companies found
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
